// One page per menu item (owner, 5 Nov 2026). Text for each page comes from admin → "หน้าเว็บ".
import { useState } from 'react';
import { Icon } from '../components/Decor';
import { PolicyBody } from '../shared/Policy';
import {
  ARTICLE_CAT_LABEL, CLIP_CAT_LABEL, COURSE_STATUS_LABEL, ENROLL_LABEL, LEVEL_LABEL, LEVEL_NUM, thaiDate, thb,
  type ArticleCat, type Enrollment, type ClipCat, type PageKey, type PageText, type SiteContent,
} from '../shared/types';
import { Courses, HoroChecker, Join, goJoin, type Auth, type Notify } from '../App';
import { ArticleCard, ClipCard, DemoBadge, LockBadge, NewsCard } from './cards';
import { api } from '../api';
import { Link } from './router';
import { PILLARS } from '../content';

const FALLBACK_PAGE: Record<PageKey, PageText> = {
  home: { title: 'โหราเทรดเดอร์', subtitle: '', body: '', cover: '' },
  courses: { title: 'คอร์สสอนเทรด', subtitle: 'เรียนเป็นขั้นตอน จากพื้นฐานสู่ขั้นสูง', body: '', cover: '' },
  horoscope: { title: 'เช็กดวงการเทรด', subtitle: 'เลือกวัน เดือน ปี เพื่อดูดาวเข้า ดาวย้ายออก', body: '', cover: '' },
  'black-trade': { title: 'Black Trade', subtitle: 'ย้อนกราฟในอดีตและจำลองการตัดสินใจ', body: '', cover: '' },
  'demo-trade': { title: 'Demo Trade', subtitle: 'ฝึกด้วยพอร์ตจำลอง ไม่ใช้เงินจริง', body: '', cover: '' },
  clips: { title: 'คลังวิดีโอ', subtitle: '', body: '', cover: '' },
  articles: { title: 'บทความ', subtitle: '', body: '', cover: '' },
  news: { title: 'ข่าวสาร', subtitle: '', body: '', cover: '' },
  member: { title: 'สมาชิก', subtitle: '', body: '', cover: '' },
};

/** Admin text wins only where the admin actually typed something; blank fields keep the built-in default. */
export const pageText = (c: SiteContent | null, key: PageKey): PageText => {
  const admin = c?.pages?.[key];
  const base = FALLBACK_PAGE[key];
  if (!admin) return base;
  return {
    title: admin.title?.trim() || base.title,
    subtitle: admin.subtitle?.trim() || base.subtitle,
    body: admin.body ?? '',
    cover: admin.cover ?? '',
  };
};

export function PageHero({ eyebrow, page, crumbs }: { eyebrow: string; page: PageText; crumbs?: { to: string; label: string }[] }) {
  return (
    <header className={`page-hero ${page.cover ? 'has-cover' : ''}`}>
      {page.cover && <img className="page-hero-img" src={page.cover} alt="" />}
      <div className="container">
        {crumbs && (
          <nav aria-label="เส้นทาง" className="crumbs">
            <ol>
              <li><Link to="/">หน้าหลัก</Link></li>
              {crumbs.map((c) => <li key={c.to}><Link to={c.to}>{c.label}</Link></li>)}
            </ol>
          </nav>
        )}
        <p className="sec-eyebrow">{eyebrow}</p>
        <h1 className="page-title">{page.title}</h1>
        {page.subtitle && <p className="sec-sub">{page.subtitle}</p>}
      </div>
    </header>
  );
}

const Empty = ({ children }: { children: string }) => <p className="empty page-empty"><span className="dot" aria-hidden="true" /> {children}</p>;
const Offline = () => <Empty>ยังเชื่อมต่อระบบหลังบ้านไม่ได้ เนื้อหาจะแสดงเมื่อเปิดเว็บผ่านเซิร์ฟเวอร์</Empty>;

/* ---------------- Courses ---------------- */
export function CoursesPage({ content }: { content: SiteContent | null }) {
  const page = pageText(content, 'courses');
  return (
    <>
      <PageHero eyebrow="TRADING ACADEMY" page={page} />
      {page.body && <div className="container prose-wrap"><PolicyBody body={page.body} /></div>}
      <Courses courses={content ? content.courses : null} title={page.title} asPage />
    </>
  );
}

export function CourseDetailPage({ content, id, lineAddUrl, auth, notify }: { content: SiteContent | null; id: string; lineAddUrl: string; auth: Auth; notify: Notify }) {
  const c = content?.courses.find((x) => x.id === id);
  const [busy, setBusy] = useState(false);
  const mine = auth.enrollments.find((e) => e.courseId === id);
  // Members: record interest in THIS course on their CRM record. Guests: sign up with this course pre-selected.
  const apply = async () => {
    if (!auth.online) return notify('สมัครเรียน');
    if (!auth.member) return goJoin('course', id);
    setBusy(true);
    const r = await api<{ enrollments: Enrollment[] }>('POST', '/api/member/interest', { courseId: id });
    setBusy(false);
    if (r.ok) {
      auth.setEnrollments(r.data.enrollments);
      auth.toast('บันทึกความสนใจคอร์สแล้ว ทีมงานจะติดต่อกลับ');
    } else auth.toast(r.error);
  };
  if (!content) return <><PageHero eyebrow="TRADING ACADEMY" page={pageText(null, 'courses')} /><div className="container"><Offline /></div></>;
  if (!c) return <NotFoundPage />;
  return (
    <>
      <PageHero eyebrow={`คอร์สเทรด · ระดับ${LEVEL_LABEL[c.level]}`} page={{ title: c.title, subtitle: c.desc, body: '', cover: c.cover || '' }} crumbs={[{ to: '/courses', label: 'คอร์สเทรด' }]} />
      <div className="container detail-grid">
        <article className="detail-main">
          {c.demo && <p className="demo-line"><DemoBadge on /> ชื่อคอร์ส รายละเอียด และราคาเป็นข้อมูลตัวอย่างสำหรับสาธิตระบบ</p>}
          {c.audience && (
            <section className="detail-block">
              <h2 className="h3">เหมาะกับใคร</h2>
              <p>{c.audience}</p>
            </section>
          )}
          {c.detail && <section className="detail-block"><h2 className="h3">รายละเอียดคอร์ส</h2><PolicyBody body={c.detail} /></section>}
          {c.syllabus && c.syllabus.length > 0 && (
            <section className="detail-block">
              <h2 className="h3">เนื้อหาที่จะได้เรียน</h2>
              <ol className="syllabus">{c.syllabus.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </section>
          )}
        </article>
        <aside className="detail-side card" aria-label="ข้อมูลการสมัคร">
          <span className="track-num" aria-hidden="true">{LEVEL_NUM[c.level]}</span>
          <p className="course-price">{c.price > 0 ? thb(c.price) : 'รอประกาศราคา'}</p>
          <p><span className={`course-status cs-${c.status}`}>{COURSE_STATUS_LABEL[c.status]}</span></p>
          <ul className="course-meta">
            {c.lessons > 0 && <li><Icon name="book" /> {c.lessons} บทเรียน</li>}
            {c.hours > 0 && <li><Icon name="calendar" /> {c.hours} ชั่วโมง</li>}
          </ul>
          {mine ? (
            <p className="enroll-state"><Icon name="check" /> คอร์สของคุณ · <span className={`enroll-badge en-${mine.status}`}>{ENROLL_LABEL[mine.status]}</span></p>
          ) : c.status !== 'closed' && (
            <button type="button" className="btn btn-primary btn-lg" onClick={apply} disabled={busy}>
              {c.status === 'open' ? 'สนใจสมัครเรียน' : 'ลงชื่อรอเปิด'} <Icon name="arrow" />
            </button>
          )}
          {lineAddUrl && <a className="btn btn-outline" href={lineAddUrl} target="_blank" rel="noopener noreferrer">สอบถามทาง LINE OA</a>}
        </aside>
      </div>
    </>
  );
}

/* ---------------- Horoscope ---------------- */
export function HoroscopePage({ content, notify, auth }: { content: SiteContent | null; notify: Notify; auth: Auth }) {
  const page = pageText(content, 'horoscope');
  return (
    <>
      <PageHero eyebrow="MEMBERS ONLY" page={page} />
      <section className="section" aria-label="เช็กดวงการเทรด">
        <div className="container narrow">
          <HoroChecker notify={notify} auth={auth} />
          {page.body && <div className="prose-wrap"><PolicyBody body={page.body} /></div>}
        </div>
      </section>
    </>
  );
}

/* ---------------- Black Trade / Demo Trade ---------------- */
export function PracticePage({ content, kind, notify }: { content: SiteContent | null; kind: 'black-trade' | 'demo-trade'; notify: Notify }) {
  const page = pageText(content, kind);
  const item = PILLARS[0].items.find((i) => i.key === (kind === 'black-trade' ? 'p-black' : 'p-demo'));
  return (
    <>
      <PageHero eyebrow="TRADER" page={page} />
      <div className="container detail-grid">
        <article className="detail-main">
          {page.body ? <PolicyBody body={page.body} /> : <Empty>รอเพิ่มรายละเอียดจากระบบหลังบ้าน</Empty>}
        </article>
        <aside className="detail-side card" aria-label={`เริ่มใช้ ${page.title}`}>
          {item && <img src={item.icon} alt="" width={64} height={64} />}
          <p className="h3">{page.title}</p>
          <p className="muted small">เครื่องมือฝึกสำหรับสมาชิก ไม่ใช้เงินจริง</p>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => notify(page.title)}>เริ่มใช้ {page.title} <Icon name="arrow" /></button>
          <button type="button" className="btn btn-outline" onClick={() => goJoin('practice')}>สมัครสมาชิก</button>
        </aside>
      </div>
    </>
  );
}

/* ---------------- Clips ---------------- */
function CatFilter<T extends string>({ labels, value, onChange, label }: { labels: Record<T, string>; value: T | ''; onChange: (v: T | '') => void; label: string }) {
  const keys = Object.keys(labels) as T[];
  return (
    <div className="cat-filter" role="group" aria-label={label}>
      <button type="button" className="chip-btn" aria-pressed={value === ''} onClick={() => onChange('')}>ทั้งหมด</button>
      {keys.map((k) => <button key={k} type="button" className="chip-btn" aria-pressed={value === k} onClick={() => onChange(k)}>{labels[k]}</button>)}
    </div>
  );
}

export function ClipsPage({ content, auth }: { content: SiteContent | null; auth: Auth }) {
  const [cat, setCat] = useState<ClipCat | ''>('');
  const page = pageText(content, 'clips');
  const list = (content?.clips ?? []).filter((c) => !cat || c.category === cat);
  const onLocked = () => (auth.member ? undefined : auth.online ? auth.openLogin() : goJoin());
  return (
    <>
      <PageHero eyebrow="VIDEO LIBRARY" page={page} />
      <section className="section" aria-label="รายการคลิป">
        <div className="container">
          {page.body && <div className="prose-wrap"><PolicyBody body={page.body} /></div>}
          <CatFilter labels={CLIP_CAT_LABEL} value={cat} onChange={setCat} label="หมวดคลิป" />
          {!content ? <Offline /> : list.length === 0 ? <Empty>ยังไม่มีคลิปในหมวดนี้</Empty> : (
            <ul className="card-grid">{list.map((c) => <li key={c.id}><ClipCard clip={c} onLocked={onLocked} /></li>)}</ul>
          )}
        </div>
      </section>
    </>
  );
}

/* ---------------- Articles ---------------- */
export function ArticlesPage({ content }: { content: SiteContent | null }) {
  const [cat, setCat] = useState<ArticleCat | ''>('');
  const page = pageText(content, 'articles');
  const list = (content?.articles ?? []).filter((a) => !cat || a.category === cat);
  return (
    <>
      <PageHero eyebrow="ARTICLES" page={page} />
      <section className="section" aria-label="รายการบทความ">
        <div className="container">
          {page.body && <div className="prose-wrap"><PolicyBody body={page.body} /></div>}
          <CatFilter labels={ARTICLE_CAT_LABEL} value={cat} onChange={setCat} label="หมวดบทความ" />
          {!content ? <Offline /> : list.length === 0 ? <Empty>ยังไม่มีบทความในหมวดนี้</Empty> : (
            <ul className="card-grid">{list.map((a) => <li key={a.id}><ArticleCard item={a} /></li>)}</ul>
          )}
        </div>
      </section>
    </>
  );
}

export function ArticleDetailPage({ content, id, auth }: { content: SiteContent | null; id: string; auth: Auth }) {
  const a = content?.articles.find((x) => x.id === id);
  if (!content) return <><PageHero eyebrow="ARTICLES" page={pageText(null, 'articles')} /><div className="container"><Offline /></div></>;
  if (!a) return <NotFoundPage />;
  return (
    <>
      <PageHero eyebrow={ARTICLE_CAT_LABEL[a.category]} page={{ title: a.title, subtitle: a.excerpt, body: '', cover: a.cover }} crumbs={[{ to: '/articles', label: 'บทความ' }]} />
      <div className="container narrow article-body">
        <p className="cms-meta"><time dateTime={a.date}>{thaiDate(a.date)}</time> <LockBadge on={a.membersOnly} /> <DemoBadge on={a.demo} /></p>
        {a.locked ? (
          <div className="card locked-article">
            <span className="lock-orb"><Icon name="lock" /></span>
            <p className="h3">บทความนี้สำหรับสมาชิก</p>
            <p className="muted">สมัครสมาชิกหรือเข้าสู่ระบบเพื่ออ่านต่อ</p>
            <div className="lock-actions">
              <button type="button" className="btn btn-primary" onClick={() => goJoin()}>สมัครสมาชิก</button>
              {auth.online && <button type="button" className="btn btn-ghost" onClick={auth.openLogin}>เข้าสู่ระบบ</button>}
            </div>
          </div>
        ) : <PolicyBody body={a.body} />}
        <Link to="/articles" className="link-btn">← กลับไปหน้าบทความ</Link>
      </div>
    </>
  );
}

/* ---------------- News ---------------- */
export function NewsPage({ content }: { content: SiteContent | null }) {
  const page = pageText(content, 'news');
  const list = content?.news ?? [];
  return (
    <>
      <PageHero eyebrow="DAILY NEWS" page={page} />
      <section className="section" aria-label="รายการข่าวสาร">
        <div className="container">
          {page.body && <div className="prose-wrap"><PolicyBody body={page.body} /></div>}
          {!content ? <Offline /> : list.length === 0 ? <Empty>ยังไม่มีข่าวสาร</Empty> : (
            <ul className="card-grid">{list.map((n) => <li key={n.id}><NewsCard item={n} /></li>)}</ul>
          )}
        </div>
      </section>
    </>
  );
}

export function NewsDetailPage({ content, id }: { content: SiteContent | null; id: string }) {
  const n = content?.news.find((x) => x.id === id);
  if (!content) return <><PageHero eyebrow="DAILY NEWS" page={pageText(null, 'news')} /><div className="container"><Offline /></div></>;
  if (!n) return <NotFoundPage />;
  return (
    <>
      <PageHero eyebrow={thaiDate(n.date)} page={{ title: n.title, subtitle: '', body: '', cover: n.cover }} crumbs={[{ to: '/news', label: 'ข่าวสาร' }]} />
      <div className="container narrow article-body">
        <p className="cms-meta"><DemoBadge on={n.demo} /></p>
        <PolicyBody body={n.body} />
        <Link to="/news" className="link-btn">← กลับไปหน้าข่าวสาร</Link>
      </div>
    </>
  );
}

/* ---------------- Member / privacy / 404 ---------------- */
export function MemberPage({ content, notify, auth }: { content: SiteContent | null; notify: Notify; auth: Auth }) {
  const page = pageText(content, 'member');
  return (
    <>
      <PageHero eyebrow="MEMBERSHIP" page={page} />
      <Join notify={notify} privacy={content?.privacy ?? null} auth={auth} courses={content?.courses ?? []} />
      {content?.lineAddUrl && (
        <div className="container center-cta">
          <a className="btn btn-outline" href={content.lineAddUrl} target="_blank" rel="noopener noreferrer">เพิ่มเพื่อน LINE OA โหราเทรดเดอร์</a>
        </div>
      )}
    </>
  );
}

export function PrivacyPage({ content }: { content: SiteContent | null }) {
  const p = content?.privacy;
  return (
    <>
      <PageHero eyebrow="PRIVACY" page={{ title: p?.title ?? 'นโยบายความเป็นส่วนตัว', subtitle: p ? `ปรับปรุงล่าสุด ${thaiDate(p.updatedAt.slice(0, 10), false)}` : '', body: '', cover: '' }} />
      <div className="container narrow article-body">
        {!p ? <Offline /> : p.body.trim() ? <PolicyBody body={p.body} /> : <Empty>ยังไม่ได้เผยแพร่นโยบายความเป็นส่วนตัว</Empty>}
      </div>
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <PageHero eyebrow="404" page={{ title: 'ไม่พบหน้านี้', subtitle: 'ลิงก์อาจถูกย้ายหรือลบไปแล้ว', body: '', cover: '' }} />
      <div className="container center-cta"><Link to="/" className="btn btn-primary">กลับหน้าหลัก</Link></div>
    </>
  );
}
